import React from 'react'
import {
  Donation,
  DonationProps,
  DonationBlockOnInitialization,
  MAX_DONATION_AMOUNT,
  MIN_DONATION_AMOUNT,
  FREQUENCY,
  type DonationFrequency,
  type DonationAmount,
  DonationVariant,
} from '@classy/campaign-page-blocks'
import { useRouter } from 'next/router'
import { trackDonationFormInitEvent } from 'services/analytics/events/donationFormInitialization'
import { useFetchProgramDesignations } from './useFetchProgramDesignations'
import { useIntelligentAskOverrides } from './useIntelligentAskOverrides'
import { usePassportCurrencyConversion } from './usePassportCurrencyConversion'
import { getJsonSessionItem, setJsonSessionItem } from 'utils/session-storage'
import { logger } from 'utils/logger'
import { useFeatureFlag } from 'utils/optimizely/hooks/useFeatureFlag'
import { OPTIMIZELY_FEATURE_FLAGS } from 'models/optimizely'

interface DonationControllerProps extends DonationProps {
  sectionId?: string
}

/**
 * Donation block properties for tracking purposes.
 * See trackDonationFormInitEvent()
 */
export type DonationInitBlockState = DonationBlockOnInitialization

/**
 * Additional IAA related properties for tracking purposes.
 * See trackDonationFormInitEvent()
 */
export interface DonationInitIntelligentAskState {
  enabled: boolean
  onetimeEnabled: boolean
  recurringEnabled: boolean
  donationOtherAmount: number | null
  recurringOtherAmount: number | null
}

/**
 * Client-side wrapper for the Donation block.
 *
 * Note, the `block*` props refer to the block values compiled in getServersideProps.
 * The block values may have been overridden by URL query parameters (see donation.ts).
 */
export const DonationController = ({
  donationAmounts: blockDonationAmounts,
  recurringAmounts: blockRecurringAmounts,
  donationFrequencies: blockDonationFrequencies,
  currencyCode = 'USD',
  minDonationAmount: blockMinDonationAmount = MIN_DONATION_AMOUNT,
  maxDonationAmount: blockMaxDonationAmount = MAX_DONATION_AMOUNT,
  sectionId,
  campaignId,
  initialProgramDesignations,
  ...rest
}: DonationControllerProps) => {
  const router = useRouter()
  const { query } = router
  let urlAmountUsed = query.amount as string

  if (
    !blockDonationAmounts?.length ||
    !blockRecurringAmounts?.length ||
    !blockDonationFrequencies?.length
  ) {
    throw new Error(
      'DonationController expects donationAmounts, recurringAmounts, and donationFrequencies to be defined.',
    )
  }

  // Intelligent Ask Amount
  const {
    donationAmounts: iaaAdjustedDonationAmounts,
    recurringAmounts: iaaAdjustedRecurringAmounts,
    donationFrequencies,
    showRecurringNudgeTag,
    isLoading: iaaIsLoading,
    intelligentAskEnabled,
    intelligentAskAmountData,
  } = useIntelligentAskOverrides(
    blockDonationAmounts || [],
    blockRecurringAmounts || [],
    blockDonationFrequencies || [],
  )

  // Currency Conversion
  const {
    donationAmounts,
    recurringAmounts,
    minDonationAmount,
    maxDonationAmount,
    currencyCode: convertedCurrencyCode,
    isLoading: currencyIsLoading,
  } = usePassportCurrencyConversion(
    iaaAdjustedDonationAmounts,
    iaaAdjustedRecurringAmounts,
    blockMinDonationAmount,
    blockMaxDonationAmount,
    currencyCode,
  )

  const onInitialization = (initBlockstate: DonationBlockOnInitialization) => {
    const intelligentAskState = {
      enabled: intelligentAskEnabled,
      onetimeEnabled: Boolean(intelligentAskAmountData.onetimeEnabled),
      recurringEnabled: Boolean(intelligentAskAmountData.recurringEnabled),
      donationOtherAmount: intelligentAskAmountData.suggested_donation_amount,
      recurringOtherAmount: intelligentAskAmountData.suggested_recurring_amount,
    }

    /**
     * When IAA is enabled, the amount query param is ignored.
     * Don't send it as part of the analytics event.
     */
    if (intelligentAskEnabled) {
      urlAmountUsed = ''
    }

    trackDonationFormInitEvent({
      initBlockstate,
      urlAmountUsed,
      sectionId,
      intelligentAskState,
    })

    const CAMPAIGN_STATS_KEY = `campaign_${campaignId}_stats`

    try {
      // * Skip writing campaign stats object if it already exists (probably from a differnt block)
      if (!getJsonSessionItem(CAMPAIGN_STATS_KEY)) {
        // * Begin writing campaign stats into session storage for tracking across donation funnel
        const donationFrequencyDefault = donationFrequencies?.find(
          (f: DonationFrequency) => f.isDefault,
        )?.name

        const donationAmountDefault =
          donationFrequencyDefault === FREQUENCY.ONE_TIME
            ? donationAmounts?.find((a: DonationAmount) => a.isDefault)?.amount
            : recurringAmounts?.find((r: DonationAmount) => r.isDefault)?.amount

        // * Report final donation grid amount arrays for IAA and exclude 0 used for "other"
        const donationOnetimeAmounts = (donationAmounts ?? [])
          .map((a) => a.amount)
          .filter((n) => Number.isFinite(n) && n > 0)
        const donationRecurringAmounts = (recurringAmounts ?? [])
          .map((r) => r.amount)
          .filter((n) => Number.isFinite(n) && n > 0)

        const donationBlockStats = {
          donationLocation: sectionId,
          donationCurrencyDefault: convertedCurrencyCode,
          donationFrequencyDefault,
          donationAmountDefault,
          recurringNudge: showRecurringNudgeTag ?? false,
          donationOnetimeAmounts,
          donationRecurringAmounts,
        }

        setJsonSessionItem(CAMPAIGN_STATS_KEY, donationBlockStats)
      }
    } catch (e) {
      logger(
        'warn',
        new Error(`Unable to write ${CAMPAIGN_STATS_KEY} object into sessionStorage`, {
          cause: e,
        }),
        {
          context: { campaignId },
        },
      )
    }
  }

  const { fetchProgramDesignations } = useFetchProgramDesignations(
    campaignId,
    initialProgramDesignations,
  )

  type DonationVariantKey = DonationVariant[number]

  const variant: DonationVariantKey[] = []

  /**
   * Easy cancel experiment: CL-52876
   */
  const easyCancelFeatureFlag = useFeatureFlag(OPTIMIZELY_FEATURE_FLAGS.npo_easy_cancel)
  const allowedEasyCancelVariations: ReadonlyArray<DonationVariantKey> = [
    'easy_cancel_with_tooltip',
    'easy_cancel_with_link',
  ]
  const easyCancelVariation = easyCancelFeatureFlag?.enabled
    ? allowedEasyCancelVariations.find((v) => v === easyCancelFeatureFlag.variationKey)
    : undefined
  if (easyCancelVariation) {
    variant.push(easyCancelVariation)
  }

  return (
    <Donation
      {...rest}
      isLoading={iaaIsLoading || currencyIsLoading}
      campaignId={campaignId}
      currencyCode={convertedCurrencyCode}
      donationAmounts={donationAmounts}
      recurringAmounts={recurringAmounts}
      donationFrequencies={donationFrequencies}
      showRecurringNudgeTag={showRecurringNudgeTag}
      minDonationAmount={minDonationAmount}
      maxDonationAmount={maxDonationAmount}
      onInitialization={onInitialization}
      initialProgramDesignations={initialProgramDesignations}
      fetchProgramDesignations={fetchProgramDesignations}
      variant={variant}
    />
  )
}
